import React, { useState } from 'react';
import RequestRepair from '../components/RequestRepair/RequestRepair';
import OrderReceived from '../components/OrderReceived/OrderReceived';
import FloatingButton from '../components/FloatingButton/FloatingButton';

export default function PrivacyPolicyPage() {
  const [showRepair, setShowRepair] = useState(false);
  const [showOrderReceived, setShowOrderReceived] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const handleRepairSubmit = (submittedOrderId) => {
    setOrderId(submittedOrderId);
    setShowRepair(false);
    setShowOrderReceived(true);
  };

  return (
    <>
      <div className="policy-page-wrapper">
        {/* Background Ambient Elements */}
        <div className="policy-bg-glow" aria-hidden="true" />
        <div className="policy-bg-dots-left" aria-hidden="true" />
        <div className="policy-bg-dots-right" aria-hidden="true" />

        <div className="policy-container">
          <header className="policy-header">
            <div className="policy-badge">
              <span className="star-icon">★</span>
              <span>LEGAL &amp; PRIVACY</span>
            </div>

            <h1>
              Privacy <span className="gold-text">Policy</span>
            </h1>

            {/* Gold Diamond Divider */}
            <div className="heading-divider" aria-hidden="true">
              <div className="divider-line" />
              <div className="divider-diamond" />
              <div className="divider-line" />
            </div>

            <p className="subnote">Independent Repair Service • Not affiliated with any brand</p>
            <p className="effective-date">Effective Date: 07/08/2026</p>
          </header>

          <div className="policy-content">
            <p className="intro">
              At DoorStep Maintenance &amp; Repair (DSMR), we respect your privacy and are committed to safeguarding your personal data. This Privacy Policy outlines how we collect, process, store, and protect your information when you visit our website or engage our repair services.
            </p>
            <p className="intro">
              By accessing our website, requesting a repair quote, or booking a doorstep appointment, you consent to the data practices described in this Policy.
            </p>

            <section className="policy-section">
              <h2>1. Information We Collect</h2>
              <p>We collect personal information necessary to fulfill service requests, process payments, and provide post-service warranty support:</p>
              <ul>
                <li><strong>Contact Information:</strong> Full name, phone number, WhatsApp contact, email address, and complete doorstep service address.</li>
                <li><strong>Device Information:</strong> Brand, model, serial number/IMEI (if provided for diagnostic verification), issue description, passcodes (only if authorized for post-repair testing).</li>
                <li><strong>Payment Information:</strong> Transaction IDs and payment confirmation status. Digital payments are processed securely via third-party payment gateways; DSMR does not store credit/debit card numbers, UPI PINs, or banking credentials.</li>
                <li><strong>Technical &amp; Usage Data:</strong> IP address, device type, browser model, operating system, and website usage statistics collected via cookies or web analytics.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>2. How We Use Your Information</h2>
              <p>Your data is processed strictly for legitimate operational purposes, including:</p>
              <ul>
                <li>Scheduling, dispatching technicians, and executing doorstep repairs.</li>
                <li>Communicating repair estimates, appointment statuses, and invoice generation.</li>
                <li>Validating and servicing doorstep warranty claims.</li>
                <li>Enhancing website user experience, diagnostic accuracy, and service quality.</li>
                <li>Complying with statutory tax, accounting, and legal requirements.</li>
              </ul>
              <p>We do not sell, rent, trade, or monetize your personal data to third parties for marketing purposes.</p>
            </section>

            <section className="policy-section">
              <h2>3. Data Protection &amp; On-Site Device Privacy</h2>
              <ul>
                <li><strong>Personal Media &amp; Data:</strong> Our technicians are strictly trained to refrain from viewing, copying, or accessing personal photos, messages, files, or applications. Passcode access requested for pre/post repair testing is restricted strictly to functional testing (e.g., display response, camera, audio).</li>
                <li><strong>Technical Security:</strong> We utilize encrypted transmission channels (HTTPS/TLS) and secure internal data management to prevent unauthorized access, alteration, or disclosure.</li>
                <li>While we take comprehensive protective measures, customers acknowledge that online data transmission carries inherent risks.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>4. Disclosure &amp; Sharing of Data</h2>
              <p>We disclose customer information only under the following limited conditions:</p>
              <ul>
                <li><strong>Assigned Technicians:</strong> Contact and address details shared with assigned doorstep service personnel solely for appointment fulfillment.</li>
                <li><strong>Third-Party Service Providers:</strong> Trusted partners who enable core functionality, including payment gateways, SMS notification vendors, and logistics support.</li>
                <li><strong>Legal Compliance:</strong> When required by court order, law enforcement, or statutory regulatory directives under Indian law.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>5. Data Retention &amp; Deletion</h2>
              <p>We retain customer records and repair service logs for as long as necessary to honor warranty obligations (ranging up to 12 months) and meet statutory accounting regulations. Outdated records are systematically purged or anonymized.</p>
            </section>

            <section className="policy-section">
              <h2>6. Cookies &amp; Tracking Technologies</h2>
              <ul>
                <li>Our website uses essential and analytics cookies to optimize navigation, remember user preferences, and analyze site traffic.</li>
                <li>You can manage or disable non-essential cookies via your browser settings, though certain site features (such as quote calculation forms) may experience reduced functionality.</li>
              </ul>
            </section>

            <section className="policy-section">
              <h2>7. Your Privacy Rights</h2>
              <p>Subject to applicable regulations (including the Digital Personal Data Protection Act of India), you have the right to:</p>
              <ul>
                <li>Request access to the personal information we maintain about you.</li>
                <li>Request correction or updating of inaccurate personal records.</li>
                <li>Request erasure of your personal data when no longer needed for warranty or legal compliance.</li>
                <li>Opt out of non-essential promotional SMS or email communications.</li>
              </ul>
              <p>To exercise any of these rights, please submit a request to our privacy team via email.</p>
            </section>

            <section className="policy-section">
              <h2>8. Protection of Minors</h2>
              <p>Our website and repair services are directed at legal adults (18 years of age or older). We do not knowingly collect personal data from minors. If a minor has provided us with personal information without parental consent, please contact us immediately to have the data removed.</p>
            </section>

            <section className="policy-section">
              <h2>9. External Web Links</h2>
              <p>Our site may contain links to external web portals or payment services. DSMR is not responsible for the privacy practices, security protocols, or content of third-party platforms.</p>
            </section>

            <section className="policy-section">
              <h2>10. Updates to This Privacy Policy</h2>
              <p>We may update this Privacy Policy periodically to reflect operational, technological, or legal developments. The revised policy will be published with an updated "Effective Date." Continued use of our platform constitutes acceptance of the updated policy terms.</p>
            </section>

            <section className="policy-section">
              <h2>11. Contact Us &amp; Privacy Complaints</h2>
              <p>For any questions, concerns, or requests regarding your personal data and privacy, please contact:</p>
              <div className="contact-info">
                <p><strong>DSMR Solutions — Privacy Team</strong></p>
                <p>Website: dsmrsolutions.in</p>
                <p>Phone: +91.8700319769</p>
                <p>Email: support@dsmrsolutions.in</p>
              </div>
            </section>
          </div>
        </div>

        {showRepair && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.4)',
            zIndex: 3000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <RequestRepair
              isModal={true}
              onClose={() => setShowRepair(false)}
              onSubmit={handleRepairSubmit}
            />
          </div>
        )}

        {showOrderReceived && (
          <OrderReceived
            orderId={orderId}
            onClose={() => setShowOrderReceived(false)}
          />
        )}



        <style>{`
          .policy-page-wrapper {
            min-height: 100vh;
            background: linear-gradient(135deg, #f9f4ee 0%, #fbf6f1 35%, #fdf8f5 70%, #fdf9f4 100%);
            padding: 8rem 1.5rem 4rem 1.5rem;
            position: relative;
            overflow: hidden;
            font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }

          .policy-bg-glow {
            position: absolute;
            top: -20px;
            left: 50%;
            transform: translateX(-50%);
            width: 900px;
            height: 480px;
            background: radial-gradient(ellipse at center, rgba(235, 205, 155, 0.35) 0%, rgba(250, 246, 240, 0) 70%);
            pointer-events: none;
            z-index: 0;
          }

          .policy-bg-dots-left {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            width: 300px;
            background-image: radial-gradient(#C99E54 1.4px, transparent 1.4px);
            background-size: 22px 22px;
            opacity: 0.25;
            mask-image: linear-gradient(to right, rgba(0,0,0,0.8), transparent);
            -webkit-mask-image: linear-gradient(to right, rgba(0,0,0,0.8), transparent);
            pointer-events: none;
          }

          .policy-bg-dots-right {
            position: absolute;
            top: 0;
            bottom: 0;
            right: 0;
            width: 300px;
            background-image: radial-gradient(#C99E54 1.4px, transparent 1.4px);
            background-size: 22px 22px;
            opacity: 0.25;
            mask-image: linear-gradient(to left, rgba(0,0,0,0.8), transparent);
            -webkit-mask-image: linear-gradient(to left, rgba(0,0,0,0.8), transparent);
            pointer-events: none;
          }

          .policy-container {
            max-width: 860px;
            margin: 0 auto;
            background: linear-gradient(135deg, #fefaf4 0%, #fdf9f3 35%, #fefaf6 70%, #fdf9f6 100%);
            border-radius: 24px;
            border: 1px solid rgba(201, 158, 84, 0.25);
            box-shadow: inset 0 1px 2px rgba(255,255,255,.95), 0 0 0 1px rgba(255,233,194,.45), 0 10px 24px rgba(191,160,110,.10), 0 14px 35px rgba(180,150,100,.09), 0 50px 100px rgba(180,150,100,.06);
            overflow: hidden;
            position: relative;
            z-index: 1;
          }

          .policy-header {
            background: linear-gradient(180deg, #FFFFFF 0%, #FAF5EE 100%);
            color: #0F172A;
            padding: 3rem 2.5rem 2rem 2.5rem;
            text-align: center;
            border-bottom: 1px solid rgba(201, 158, 84, 0.2);
          }

          .policy-badge {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 0.75rem;
            font-weight: 700;
            letter-spacing: 1.5px;
            color: #C99E54;
            background: rgba(201, 158, 84, 0.08);
            border: 1px solid rgba(201, 158, 84, 0.3);
            border-radius: 30px;
            padding: 6px 16px;
            margin-bottom: 1rem;
            text-transform: uppercase;
          }

          .star-icon {
            font-size: 0.85rem;
            color: #F0B040;
          }

          .policy-header h1 {
            font-size: 2.75rem;
            font-weight: 800;
            color: #0F172A;
            margin: 0 0 0.5rem 0;
            letter-spacing: -0.5px;
          }

          .gold-text {
            color: #C98E32;
            background: linear-gradient(135deg, #D99215 0%, #B88228 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }

          .heading-divider {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            margin: 1rem 0 1.25rem 0;
          }

          .divider-line {
            height: 1px;
            width: 60px;
            background: linear-gradient(90deg, transparent, #C99E54, transparent);
          }

          .divider-diamond {
            width: 7px;
            height: 7px;
            background: #C99E54;
            transform: rotate(45deg);
          }

          .subnote {
            margin: 0 0 0.4rem 0;
            color: #64748B;
            font-size: 0.98rem;
            font-weight: 500;
          }

          .effective-date {
            font-size: 0.95rem;
            color: #94A3B8;
            margin: 0;
            font-weight: 500;
          }

          .policy-content {
            padding: 3rem 2.5rem;
            line-height: 1.8;
            color: #334155;
          }

          .intro {
            font-size: 1.08rem;
            margin-bottom: 1.5rem;
            padding: 1.5rem;
            background: rgba(201, 158, 84, 0.06);
            border-left: 4px solid #C99E54;
            border-radius: 0 12px 12px 0;
            color: #1E293B;
          }

          .policy-section {
            margin-bottom: 2.75rem;
          }

          .policy-section h2 {
            color: #0F172A;
            font-size: 1.45rem;
            font-weight: 700;
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid rgba(201, 158, 84, 0.18);
            display: flex;
            align-items: center;
          }

          .policy-section p {
            margin-bottom: 1rem;
            font-size: 1.02rem;
            color: #334155;
          }

          .policy-section ul,
          .policy-section ol {
            margin: 1rem 0 1.25rem 0;
            padding-left: 1.6rem;
          }

          .policy-section li {
            margin-bottom: 0.6rem;
            font-size: 1.02rem;
            color: #334155;
          }

          .contact-info {
            background: linear-gradient(135deg, #FFFDF9 0%, #FDF8F0 100%);
            padding: 1.75rem;
            border-radius: 14px;
            border: 1px solid rgba(201, 158, 84, 0.25);
            box-shadow: 0 4px 15px rgba(201, 158, 84, 0.06);
            margin-top: 1.25rem;
          }

          .contact-info p {
            margin: 0.5rem 0;
            font-size: 1.02rem;
          }

          .contact-info strong {
            color: #0F172A;
          }

          @media (max-width: 768px) {
            .policy-page-wrapper {
              padding: 6.5rem 1rem 2rem 1rem;
            }

            .policy-container {
              border-radius: 16px;
            }

            .policy-header {
              padding: 2.25rem 1.5rem 1.5rem 1.5rem;
            }

            .policy-header h1 {
              font-size: 2.1rem;
            }

            .policy-content {
              padding: 2rem 1.5rem;
            }

            .intro {
              font-size: 1rem;
              padding: 1.15rem;
            }

            .policy-section h2 {
              font-size: 1.3rem;
            }

            .policy-section p,
            .policy-section li {
              font-size: 0.98rem;
            }
          }

          @media (max-width: 480px) {
            .policy-page-wrapper {
              padding: 5.5rem 0.5rem 1.5rem 0.5rem;
            }

            .policy-header {
              padding: 1.75rem 1rem 1.25rem 1rem;
            }

            .policy-header h1 {
              font-size: 1.85rem;
            }

            .policy-content {
              padding: 1.5rem 1rem;
            }
          }
        `}</style>
      </div>
    </>
  );
}
